import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isIos, isStandalone, useInstallStore } from '../../store/installStore'
import ParrotMark from './ParrotMark'

const DISMISSED_KEY = 'shadowtooti.installPromptDismissed'

/**
 * A custom install banner, because the platforms diverge in a way no single
 * built-in prompt covers:
 *
 * - Chrome/Edge/Android fire `beforeinstallprompt`, which the install store
 *   captures and defers — calling `.prompt()` from a button now instead of
 *   whatever moment the browser chose is exactly what that event exists for.
 *   The store holds it, not this component, because the download page
 *   offers the same prompt and the event can be spent only once.
 * - iOS Safari never fires it at all (no engine-level install prompt
 *   exists there), so the only way to install is the Share-sheet's own
 *   "Add to Home Screen", which a user has no reason to know about unless
 *   the app tells them.
 */
export default function InstallPrompt() {
  const { t } = useTranslation('common')
  const deferredEvent = useInstallStore((s) => s.deferredEvent)
  const prompt = useInstallStore((s) => s.prompt)
  // Hidden until the effect has looked at the device and storage, so the
  // banner never flashes for someone who dismissed it or is inside the app.
  const [dismissed, setDismissed] = useState(true)
  const [showIosHint, setShowIosHint] = useState(false)

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISSED_KEY)) return
    setDismissed(false)
    if (isIos()) setShowIosHint(true)
  }, [])

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, '1')
    setDismissed(true)
    setShowIosHint(false)
  }

  if (dismissed || (!deferredEvent && !showIosHint)) return null

  return (
    // brand-* are theme tokens (index.css) that already repaint in dark
    // mode on their own — no dark: prefixes needed here.
    <div className="border-b border-brand-200 bg-brand-50 px-4 py-2.5 text-sm">
      <div className="mx-auto flex max-w-4xl items-center gap-3">
        <ParrotMark size={22} className="shrink-0" />
        <p className="flex-1 text-brand-800">{showIosHint ? t('install.iosHint') : t('install.prompt')}</p>
        {!showIosHint && (
          <button
            type="button"
            onClick={prompt}
            className="shrink-0 rounded-lg bg-brand-600 px-3 py-1.5 font-medium text-white dark:text-slate-900"
          >
            {t('install.installButton')}
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('install.closeAriaLabel')}
          className="shrink-0 text-brand-800/60 hover:text-brand-800"
        >
          ✕
        </button>
      </div>
    </div>
  )
}
